import { useEffect } from "react";
import { Box, Flex } from "styled-system/jsx";
import { Spinner, Text } from "@ttc3k/trekker";
import { i18nLanguage } from "i18n";
import { useTripperData } from "./hooks/useTripperData";
import { TripperLayout } from "./components/TripperLayout";
import { useAutoFollow } from "./hooks/useAutoFollow";
import { useTripperWidgetContext } from "./context/TripperWidgetContext";
import { TripperDisplayMode } from "./types";
import { applyAccentColorToDiv } from "./accentColorHelpers";

type TripperWidgetProps = {
  itineraryId: string;
  language: i18nLanguage;
  displayMode: TripperDisplayMode;
  onStopDetailsOpenChange?: (isOpen: boolean) => void;
};

export const TripperWidget = ({
  itineraryId,
  language,
  displayMode,
  onStopDetailsOpenChange,
}: TripperWidgetProps) => {
  const { data, isLoading, error } = useTripperData(itineraryId);
  const { accentDivRef } = useTripperWidgetContext();

  useAutoFollow({ data });

  // When the itinerary data loads, apply its primaryColor to the accent div
  // via direct DOM mutation.  This avoids a state change in the provider tree
  // (which would cause StyleRoot to recreate its Shadow DOM and remount everything).
  // The inline styles set here are lower priority than any explicit
  // primary-color attribute, because the provider only sets the CSS
  // vars via inline style when primaryColorOverride is defined — and inline
  // styles on the same element always win over later mutations of the same
  // properties.  We therefore only write when no override was already set.
  const itineraryColor = data.itinerary?.tripperSettings?.primaryColor;
  useEffect(() => {
    const div = accentDivRef.current;
    if (!div || !itineraryColor) return;

    // Don't overwrite if an explicit widget-level override is already present
    // (the provider sets --tripper-primary via inline style when an override exists).
    const existing = div.style.getPropertyValue("--tripper-primary");
    if (existing && existing !== "#66800b") return;

    applyAccentColorToDiv(div, itineraryColor);
  }, [itineraryColor, accentDivRef]);

  if (isLoading) {
    return (
      <Flex h={"100%"} alignItems={"center"} justifyContent={"center"}>
        <Spinner width={"40px"} height={"40px"} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex h={"100%"} alignItems={"center"} justifyContent={"center"}>
        <Text visual={"body"}>Unable to load itinerary</Text>
      </Flex>
    );
  }

  if (!data.itinerary) {
    return (
      <Flex h={"100%"} alignItems={"center"} justifyContent={"center"}>
        <Box
          color="#6F6E69"
          fontSize="15px"
          fontFamily="var(--tripper-font-family)"
        >
          This itinerary is no longer available.
        </Box>
      </Flex>
    );
  }

  const handleRootClick = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (anchor?.href && !anchor.target?.includes("_blank")) {
      e.preventDefault();
      window.open(anchor.href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box className="tripper-widget-root" onClick={handleRootClick}>
      <TripperLayout
        data={data}
        language={language}
        displayMode={displayMode}
        onStopDetailsOpenChange={onStopDetailsOpenChange}
      />
    </Box>
  );
};

export default TripperWidget;
